import React from 'react';
import './index.scss';
import classNames from 'classnames';
import { IProgressBarProps } from './type';

/** 按色块宽度分档透明度：0~25% / 25~50% / 50~75% / 75~100% */
const WIDTH_OPACITY_LEVELS = [0.08, 0.2, 0.32, 0.5] as const;

/** 将 #RGB / #RRGGBB / #RRGGBBAA 解析为 RGB，忽略原有 alpha */
const parseHexRgb = (color: string): { r: number; g: number; b: number; } | null => {
  const raw = color.trim();
  if (!raw.startsWith('#')) return null;
  const hex = raw.slice(1);
  let full = hex;
  if (hex.length === 3) {
    full = hex.split('').map(c => c + c).join('');
  } else if (hex.length === 8) {
    full = hex.slice(0, 6);
  } else if (hex.length !== 6) {
    return null;
  }
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return null;
  return { r, g, b };
};

/** 按最终色块宽度落入的区间，取对应透明度档位 */
const resolveOpacityByWidth = (widthPercent: number): number => {
  const w = Math.min(Math.max(widthPercent, 0), 100);
  if (w < 25) return WIDTH_OPACITY_LEVELS[0];
  if (w < 50) return WIDTH_OPACITY_LEVELS[1];
  if (w < 75) return WIDTH_OPACITY_LEVELS[2];
  return WIDTH_OPACITY_LEVELS[3];
};

/** 根据最终色块宽度生成带透明度的颜色 */
const colorByWidth = (baseColor: string, widthPercent: number): string => {
  const rgb = parseHexRgb(baseColor) || { r: 50, g: 111, b: 240 };
  const opacity = resolveOpacityByWidth(widthPercent);
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

export const ProgressBar = (props: IProgressBarProps) => {
  const {
    value,
    widthValue,
    mode = 'left',
    height = 22,
    colorByColumnRange = false,
  } = props;

  // 优先使用显式传入的 widthValue（已为 0~100 的百分比绝对值），否则按 value 计算
  const resolveWidthPercent = (): number => {
    if (typeof widthValue === 'number' && Number.isFinite(widthValue)) {
      return Math.min(Math.max(Math.abs(widthValue), 0), 100);
    }
    return Math.min(Math.abs(value), 100);
  };

  const widthPercent = resolveWidthPercent();

  const renderLeft = () => {
    // 仅 colorByColumnRange 开启时按最终 width 分档透明度；未开启保持历史固定色
    const { positiveColor = '#326FF026' } = props;
    const backgroundColor = colorByColumnRange
      ? colorByWidth(positiveColor === '#326FF026' ? '#326FF0' : positiveColor, widthPercent)
      : positiveColor;

    return (
      <div
        className="dbd-common-table__progress-bar dbd-common-table__progress-bar-left"
        style={{
          width: `${widthPercent}%`,
          height: `${height}px`,
          backgroundColor,
        }}
      />
    );
  };

  const renderCenter = () => {
    const { positiveColor = '#69D19C33', negativeColor = '#F17C8633' } = props;
    const isPositive = value >= 0;
    // center 模式下，宽度上限为单边 50%（左右各占一半空间）
    const halfWidthPercent = Math.min(widthPercent / 2, 50);
    const baseColor = isPositive ? positiveColor : negativeColor;
    const backgroundColor = colorByColumnRange
      ? colorByWidth(baseColor, widthPercent)
      : baseColor;

    return (
      <div
        className={classNames(
          'dbd-common-table__progress-bar',
          'dbd-common-table__progress-bar-center',
          {
            'dbd-common-table__progress-bar-center-positive': isPositive,
            'dbd-common-table__progress-bar-center-negative': !isPositive,
          },
        )}
        style={{
          width: `${halfWidthPercent}%`,
          height: `${height}px`,
          backgroundColor,
        }}
      />
    );
  };

  return (
    <>
      {mode === 'left' && value >= 0 && renderLeft()}
      {mode === 'center' && renderCenter()}
    </>
  );
};
